import Keycloak from 'keycloak-js'
import type { KeycloakInitOptions } from 'keycloak-js'

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || '',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'sgrc',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'frontend-admin',
}

export const keycloak = new Keycloak(keycloakConfig)

// --- Single-flight refresh-token mutex -------------------------------------
// Keycloak's realm enforces a single-use refresh_token policy (rotation).
// Under React Strict-Mode double-mount, parallel axios calls, or rapid
// page.goto() storms, multiple callers can hit keycloak.updateToken() at the
// same moment. The first call POSTs the saved refresh_token; the second
// concurrent call POSTs the SAME refresh_token, which the IdP has already
// invalidated -> invalid_grant 400 -> the whole session is dropped.
//
// Wrap updateToken with a shared Promise so concurrent callers de-dupe to a
// single in-flight refresh and all await the same resolution.
const __originalUpdateToken = keycloak.updateToken.bind(keycloak)
let __pendingRefresh: Promise<boolean> | null = null
keycloak.updateToken = ((minValidity?: number) => {
  if (__pendingRefresh) return __pendingRefresh
  __pendingRefresh = __originalUpdateToken(minValidity).finally(() => {
    __pendingRefresh = null
  })
  return __pendingRefresh
}) as typeof keycloak.updateToken

let isInitialized = false
let initPromise: Promise<boolean> | null = null

export async function initKeycloakSafe(options: KeycloakInitOptions): Promise<boolean> {
  if (isInitialized) return keycloak.authenticated || false
  if (initPromise) return initPromise

  initPromise = keycloak.init(options).then((authenticated) => {
    isInitialized = true
    return authenticated
  }).catch(() => {
    isInitialized = true
    initPromise = null
    return false
  })
  return initPromise
}

export const keycloakIdpHint = import.meta.env.VITE_KC_IDP_HINT || undefined

export const keycloakInitOptions: KeycloakInitOptions = {
  onLoad: 'login-required',
  pkceMethod: 'S256',
  checkLoginIframe: false,
  enableLogging: true,
}

const DEFAULT_TENANT_ID = '00000000-0000-0000-0000-000000000001'

export function getTenantId(): string | null {
  const explicit = keycloak.tokenParsed ? (keycloak.tokenParsed as any).tenant_id : null
  if (explicit) return explicit
  // Fallback: some users (from the faithbook realm) have no tenant_id claim yet.
  // Send the default sgrc tenant so services that require X-Tenant-ID don't 400.
  return DEFAULT_TENANT_ID
}

export function getUserId(): string | null {
  if (keycloak.tokenParsed) return (keycloak.tokenParsed as any).sub || null
  return null
}

export function getUserRoles(): string[] {
  if (keycloak.tokenParsed?.realm_access?.roles) return keycloak.tokenParsed.realm_access.roles
  return []
}

export function hasRole(role: string): boolean {
  return getUserRoles().includes(role)
}

export function hasAnyRole(roles: string[]): boolean {
  const userRoles = getUserRoles()
  return roles.some((role) => userRoles.includes(role))
}

export function getPortalAccess(): string[] {
  if (keycloak.tokenParsed) {
    const raw = (keycloak.tokenParsed as any).portal_access
    if (Array.isArray(raw)) return raw
    if (typeof raw === 'string') return raw.split(',').map((s: string) => s.trim())
  }
  return []
}

export function getAccountType(): string | null {
  if (keycloak.tokenParsed) return (keycloak.tokenParsed as any).account_type || null
  return null
}

export function getTenantType(): string | null {
  if (keycloak.tokenParsed) return (keycloak.tokenParsed as any).tenant_type || null
  return null
}

export async function refreshToken(minValidity = 30): Promise<boolean> {
  try {
    const refreshed = await keycloak.updateToken(minValidity)
    return refreshed
  } catch (error) {
    console.error('Failed to refresh token', error)
    return false
  }
}
